import { useState, useEffect } from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { publicProfileData, formatProfileLocation } from '../components/instrutorProfile/profileAPIs';
import { userApi } from '../../src/config';
import { getDisplayName, getPillarMeta, getSpecialityLabel } from '../utils/breadcrumb';

export default function PractitionerProfile() {
  const { id } = useParams();
  const location = useLocation();
  const routeState = (location.state as {
    sourcePillar?: { name: string; slug: string } | string;
    sourceSpeciality?: string;
    sourceName?: string;
  }) || {};
  const sourcePillarMeta = typeof routeState.sourcePillar === 'object' ? routeState.sourcePillar : null;
  const sourcePillarName =
    sourcePillarMeta?.name ||
    (typeof routeState.sourcePillar === 'string' ? routeState.sourcePillar : '');
  const [instructor, setInstructor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [similarInstructors, setSimilarInstructors] = useState<any[]>([]);
  const [activeCategory, setActiveCategory] = useState(sourcePillarName || 'Yoga');
  const [activePanel, setActivePanel] = useState<'class' | 'consultation'>('class');
  const [activeTab, setActiveTab] = useState('about');

  const classSessions = (instructor?.upcomingClasses || []).filter((session: any) => {
    const type = String(session?.classType || '').toLowerCase();
    return ['recurring', 'group', 'class', 'both'].includes(type) || (!type && !!session?.classPrice);
  });

  const consultationSessions = (instructor?.upcomingClasses || []).filter((session: any) => {
    const type = String(session?.classType || '').toLowerCase();
    return ['fixed', 'one-on-one', 'consult', 'consultation', 'both'].includes(type);
  });

  const serviceTabs: Array<{ key: 'class' | 'consultation'; label: string }> = [
    classSessions.length > 0 ? { key: 'class', label: 'Class' } : null,
    consultationSessions.length > 0 ? { key: 'consultation', label: 'Consultation' } : null,
  ].filter(Boolean) as Array<{ key: 'class' | 'consultation'; label: string }>;

  const activeSessions = activePanel === 'consultation' ? consultationSessions : classSessions;
  const selectedSession = activeSessions[0] || classSessions[0] || consultationSessions[0] || null;
  const displayName = routeState.sourceName || getDisplayName(instructor);
  const specialityLabel = routeState.sourceSpeciality || getSpecialityLabel(instructor);
  const pillarMeta =
    (sourcePillarMeta?.slug
      ? sourcePillarMeta
      : null) || getPillarMeta([
    specialityLabel,
    activeCategory,
    instructor?.role,
    instructor?.Skills?.[0],
    instructor?.skills?.[0],
    instructor?.upcomingClasses?.[0]?.classCategory,
  ].filter(Boolean).join(' '));

  const formatSessionTime = (value: any) => {
    if (!value) return 'Soon';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Asia/Kolkata',
    }).format(date);
  };

  const scrollToSection = (sectionId: string) => {
    setActiveTab(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'qualifications', 'pricing', 'availability', 'reviews'];
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY >= el.offsetTop - 140) {
          setActiveTab(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (serviceTabs.length === 0) return;
    setActivePanel((current) => (
      serviceTabs.some((tab) => tab.key === current) ? current : serviceTabs[0].key
    ));
  }, [instructor, classSessions.length, consultationSessions.length]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (id) {
          const res = await publicProfileData(id) as any;
          if (res && res.success && res.data) {
            setInstructor({
              ...res.data,
              firstname: res.data.firstname || res.data.firstName || "Ananya",
              lastname: res.data.lastname || res.data.lastName || "Rao",
              location: formatProfileLocation(res.data.location) || "Pune, Maharashtra",
              bio: res.data.bio || "Ananya is an RYT-500 certified Yoga Instructor and Reiki Practitioner...",
              experience: res.data.experience || "8",
              languages: res.data.languages && res.data.languages.length > 0 ? res.data.languages : ["English", "Hindi", "Marathi"],
              profileIcon: res.data.profileIcon
            });
            return;
          }
        }
      } catch (err) { }

      // Keep the page honest if the profile cannot be loaded.
      setInstructor({
        firstname: routeState.sourceName?.split(' ')[0] || "Instructor",
        lastname: "",
        location: "Profile unavailable",
        experience: "0",
        languages: [],
        bio: routeState.sourceSpeciality ? `This ${routeState.sourceSpeciality} profile could not be loaded right now.` : "This public profile could not be loaded right now.",
        rating: 0,
        reviews: 0,
        role: routeState.sourceSpeciality || "Instructor",
      });
    };

    fetchProfile().finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (instructor) {
      const fetchSimilar = async () => {
        try {
          const category =
            sourcePillarName ||
            instructor.upcomingClasses?.[0]?.classCategory ||
            (instructor.skills && instructor.skills[0]) ||
            instructor.role ||
            "Yoga";
          setActiveCategory(category);
          const res = await userApi.get<any>(`/view-homepage-instructors`);
          if (res.data && res.data.data) {
            const filtered = res.data.data.filter((p: any) => p._id !== instructor._id && p.slug).slice(0, 5);
            if (filtered.length > 0) {
              setSimilarInstructors(filtered.map((p: any) => ({
                name: p.vendorName || `${p.firstname} ${p.lastname}`,
                r: `★ ${p.rating || 4.9} · ${p.role || category}`,
                c: 'var(--forest-dark)',
                i: `${p.firstname?.charAt(0) || 'M'}${p.lastname?.charAt(0) || ''}`.toUpperCase(),
                slug: p.slug,
                profileIcon: p.profileIcon
              })));
            }
          }
        } catch (e) { }
      };
      fetchSimilar();
    }
  }, [instructor]);

  if (loading || !instructor) {
    return (
      <div className="min-h-screen bg-[#FBF9F4] flex items-center justify-center py-20">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-3 border-[#1C8F86] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        :root {
          --bg-page: #FAF8F4;
          --bg-card: #FFFFFF;
          --bg-surface: #F5F2EA;
          --bg-surface-subtle: #FAF7F0;
          --border: #EDE7DD;
          --border-subtle: #F2EDE4;
          --text-heading: #14322B;
          --text-body: #4E635D;
          --text-muted: #7E928C;
          --brand-primary: #153931;
          --brand-hover: #1F4F44;
          --accent-emerald: #198777;
          --accent-gold: #E59819;
          --card-shadow: 0 4px 20px -2px rgba(20, 50, 43, 0.05), 0 2px 6px -1px rgba(0, 0, 0, 0.02);
          --card-shadow-hover: 0 12px 30px -4px rgba(20, 50, 43, 0.1), 0 4px 10px -2px rgba(0, 0, 0, 0.03);
          --sidebar-shadow: 0 10px 32px -6px rgba(20, 50, 43, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.03);
        }

        .practitioner-profile-page {
          background: var(--bg-page);
          color: var(--text-heading);
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          line-height: 1.55;
          -webkit-font-smoothing: antialiased;
          min-height: 100vh;
          padding-bottom: 40px;
        }

        @media (max-width: 768px) {
          .practitioner-profile-page {
            padding-bottom: 96px; /* Avoid mobile bottom navigation overlap */
          }
        }

        .practitioner-profile-page img,
        .practitioner-profile-page svg {
          display: block;
          max-width: 100%;
        }

        .practitioner-profile-page a {
          color: inherit;
          text-decoration: none;
        }

        .practitioner-profile-page h1,
        .practitioner-profile-page h2,
        .practitioner-profile-page h3,
        .practitioner-profile-page h4 {
          font-family: 'Fraunces', Georgia, serif;
          font-weight: 700;
          margin: 0;
          letter-spacing: -0.015em;
          color: var(--text-heading);
        }

        .practitioner-profile-page .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--accent-emerald);
          margin-bottom: 10px;
        }

        .practitioner-profile-page .eyebrow::before {
          content: "";
          width: 14px;
          height: 2px;
          background: var(--accent-emerald);
          border-radius: 2px;
        }

        .practitioner-profile-page .wrap {
          max-width: 1180px;
          margin: 0 auto;
          padding: 0 24px;
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .wrap {
            padding: 0 16px;
          }
        }

        .practitioner-profile-page .btn {
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 13.5px;
          padding: 11px 22px;
          border-radius: 9999px;
          border: 1px solid transparent;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          transition: all 0.2s ease;
          text-decoration: none !important;
        }

        .practitioner-profile-page .btn-primary {
          background: var(--brand-primary);
          color: #ffffff !important;
          box-shadow: 0 4px 14px rgba(20, 50, 43, 0.18);
        }

        .practitioner-profile-page .btn-primary:hover {
          background: var(--brand-hover);
          transform: translateY(-1.5px);
          box-shadow: var(--card-shadow-hover);
        }

        .practitioner-profile-page .btn-ghost {
          background: #ffffff;
          color: var(--text-heading);
          border-color: var(--border);
        }

        .practitioner-profile-page .btn-ghost:hover {
          background: var(--bg-surface);
          border-color: var(--text-heading);
        }

        .practitioner-profile-page .btn-block {
          width: 100%;
        }

        .practitioner-profile-page .profile-topline {
          padding: 20px 0 14px;
        }

        .practitioner-profile-page .breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
          white-space: nowrap;
          overflow-x: auto;
          scrollbar-width: none;
          width: 100%;
          font-size: 12.5px;
          color: var(--text-muted);
          font-weight: 500;
        }

        .practitioner-profile-page .breadcrumb::-webkit-scrollbar {
          display: none;
        }

        .practitioner-profile-page .breadcrumb a {
          color: var(--text-body);
          transition: color 0.18s;
        }

        .practitioner-profile-page .breadcrumb a:hover {
          color: var(--accent-emerald);
        }

        .practitioner-profile-page .breadcrumb .current {
          color: var(--text-heading);
          font-weight: 600;
        }

        .practitioner-profile-page .subnav {
          position: sticky;
          top: 0;
          z-index: 50;
          background: rgba(250, 248, 244, 0.92);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--border);
        }

        .practitioner-profile-page .subnav .wrap {
          display: flex;
          gap: 32px;
          padding: 10px 24px;
          overflow-x: auto;
          scrollbar-width: none;
        }

        .practitioner-profile-page .subnav .wrap::-webkit-scrollbar {
          display: none;
        }

        .practitioner-profile-page .subnav a {
          font-size: 13.5px;
          font-weight: 600;
          color: var(--text-body);
          white-space: nowrap;
          padding: 8px 0;
          border-bottom: 2.5px solid transparent;
          transition: all 0.18s ease;
        }

        .practitioner-profile-page .subnav a.active,
        .practitioner-profile-page .subnav a:hover {
          color: var(--text-heading);
          border-color: var(--accent-emerald);
        }

        .practitioner-profile-page .profile-hero {
          padding: 32px 0 0;
        }

        .practitioner-profile-page .hero-layout {
          display: grid;
          grid-template-columns: 1fr 340px;
          gap: 32px;
          align-items: start;
        }

        @media (max-width: 990px) {
          .practitioner-profile-page .hero-layout {
            grid-template-columns: 1fr;
          }
        }

        .practitioner-profile-page .id-block {
          display: flex;
          gap: 24px;
          align-items: center;
          margin-bottom: 24px;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 28px;
          box-shadow: var(--card-shadow);
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .id-block {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 22px 18px;
          }
        }

        .practitioner-profile-page .id-avatar {
          width: 108px;
          height: 108px;
          border-radius: 22px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Fraunces', serif;
          font-size: 34px;
          color: #ffffff;
          font-weight: 700;
          background: var(--brand-primary);
          overflow: hidden;
          box-shadow: 0 8px 24px -4px rgba(20, 50, 43, 0.15);
          border: 3px solid #ffffff;
        }

        .practitioner-profile-page .id-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .practitioner-profile-page .id-name-row {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .id-name-row {
            justify-content: center;
          }
        }

        .practitioner-profile-page .id-name-row h1 {
          font-size: clamp(24px, 3.2vw, 32px);
          line-height: 1.2;
        }

        .practitioner-profile-page .verified {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #E8F5F1;
          color: #106654;
          border: 1px solid #C0E8DD;
          font-size: 11.5px;
          font-weight: 700;
          padding: 4px 10px;
          border-radius: 9999px;
        }

        .practitioner-profile-page .id-role {
          font-size: 14.5px;
          color: var(--text-body);
          margin: 6px 0 14px;
          font-weight: 500;
        }

        .practitioner-profile-page .id-facts {
          display: flex;
          gap: 8px 10px;
          flex-wrap: wrap;
          font-size: 12.5px;
          color: var(--text-body);
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .id-facts {
            justify-content: center;
          }
        }

        .practitioner-profile-page .id-facts span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--bg-surface);
          border: 1px solid var(--border);
          padding: 5px 12px;
          border-radius: 9999px;
          font-weight: 500;
        }

        .practitioner-profile-page .id-facts .id-rating {
          color: #8F5500;
          font-weight: 600;
          background: #FFF9ED;
          border-color: #F6DFB6;
        }

        .practitioner-profile-page .profile-card-box {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 28px 32px;
          box-shadow: var(--card-shadow);
          margin-bottom: 24px;
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .profile-card-box {
            padding: 20px 18px;
            border-radius: 20px;
          }
        }

        .practitioner-profile-page .booking-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 24px;
          padding: 24px;
          position: sticky;
          top: 80px;
          box-shadow: var(--sidebar-shadow);
        }

        .practitioner-profile-page .service-toggle {
          display: flex;
          background: var(--bg-surface);
          border-radius: 9999px;
          padding: 4px;
          margin-bottom: 18px;
          gap: 4px;
          border: 1px solid var(--border);
        }

        .practitioner-profile-page .service-toggle .opt {
          flex: 1;
          text-align: center;
          padding: 9px 0;
          border-radius: 9999px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          color: var(--text-body);
          border: 0;
          background: transparent;
          transition: all 0.18s;
        }

        .practitioner-profile-page .service-toggle .opt.active {
          background: var(--brand-primary);
          color: #ffffff;
          box-shadow: 0 2px 8px rgba(20, 50, 43, 0.15);
        }

        .practitioner-profile-page .session-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .practitioner-profile-page .session-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 14px;
          border-bottom: 1px solid var(--border-subtle);
          padding-bottom: 12px;
        }

        .practitioner-profile-page .session-item:last-child {
          border-bottom: 0;
          padding-bottom: 0;
        }

        .practitioner-profile-page .session-item .title {
          font-size: 13.5px;
          font-weight: 700;
          color: var(--text-heading);
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }

        .practitioner-profile-page .session-item .meta {
          font-size: 11.5px;
          color: var(--text-muted);
          margin-top: 3px;
          line-height: 1.3;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 180px;
        }

        .practitioner-profile-page .session-item .price {
          font-family: 'Fraunces', serif;
          font-weight: 700;
          white-space: nowrap;
          font-size: 15px;
          color: var(--accent-emerald);
        }

        .practitioner-profile-page .service-muted {
          font-size: 13.5px;
          color: var(--text-body);
          line-height: 1.55;
          text-align: center;
          padding: 24px 16px;
          background: var(--bg-surface);
          border-radius: 18px;
          border: 1px solid var(--border-subtle);
          margin: 0;
        }

        .practitioner-profile-page .next-slot {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          font-weight: 600;
          color: #106654;
          background: #E8F5F1;
          border: 1px solid #C0E8DD;
          padding: 9px 13px;
          border-radius: 12px;
          margin: 16px 0 12px;
        }

        .practitioner-profile-page .dot-live {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--accent-emerald);
          flex-shrink: 0;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.9); }
        }

        .practitioner-profile-page section {
          padding: 24px 0;
          scroll-margin-top: 80px;
        }

        .practitioner-profile-page .profile-section h2 {
          font-size: 22px;
          margin-bottom: 18px;
        }

        .practitioner-profile-page .about-text {
          font-size: 15px;
          color: var(--text-body);
          line-height: 1.75;
          max-width: 760px;
          font-weight: 400;
          margin: 0;
        }

        .practitioner-profile-page .lang-chips {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          margin-top: 20px;
        }

        .practitioner-profile-page .lang-chip {
          padding: 6px 14px;
          border-radius: 9999px;
          background: #EEF6F4;
          border: 1px solid #CEE7E0;
          font-size: 12.5px;
          font-weight: 600;
          color: #166455;
        }

        .practitioner-profile-page .qual-list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 14px;
        }

        .practitioner-profile-page .qual-item {
          display: flex;
          gap: 14px;
          align-items: center;
          background: var(--bg-surface);
          border: 1px solid var(--border-subtle);
          border-radius: 18px;
          padding: 16px 20px;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .practitioner-profile-page .qual-item:hover {
          transform: translateY(-1.5px);
          box-shadow: 0 4px 12px rgba(20, 50, 43, 0.04);
        }

        .practitioner-profile-page .qual-item .ic {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid var(--border);
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 17px;
          color: var(--accent-emerald);
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
        }

        .practitioner-profile-page .qual-item .txt {
          font-size: 13px;
        }

        .practitioner-profile-page .qual-item .txt b {
          display: block;
          font-size: 14.5px;
          font-weight: 700;
          color: var(--text-heading);
          margin-bottom: 2px;
        }

        .practitioner-profile-page .qual-item .txt span {
          color: var(--text-muted);
          font-size: 12px;
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .qual-list {
            grid-template-columns: 1fr;
          }
        }

        .practitioner-profile-page .review-summary {
          display: flex;
          gap: 32px;
          align-items: center;
          margin-bottom: 28px;
          flex-wrap: wrap;
        }

        .practitioner-profile-page .rs-score {
          font-family: 'Fraunces', serif;
          font-size: 48px;
          font-weight: 700;
          color: var(--text-heading);
          line-height: 1;
        }

        .practitioner-profile-page .rs-bars {
          flex: 1;
          min-width: 200px;
        }

        .practitioner-profile-page .rs-bar-row {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          margin-bottom: 6px;
          font-weight: 600;
          color: var(--text-body);
        }

        .practitioner-profile-page .rs-bar {
          flex: 1;
          height: 7px;
          border-radius: 10px;
          background: var(--bg-surface);
          border: 1px solid var(--border-subtle);
          overflow: hidden;
        }

        .practitioner-profile-page .rs-bar-fill {
          height: 100%;
          background: var(--accent-gold);
          border-radius: 10px;
        }

        .practitioner-profile-page .review-list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .practitioner-profile-page .review-card {
          background: var(--bg-surface);
          border: 1px solid var(--border-subtle);
          border-radius: 18px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          transition: transform 0.2s ease;
        }

        .practitioner-profile-page .review-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 14px rgba(20, 50, 43, 0.04);
        }

        .practitioner-profile-page .review-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .practitioner-profile-page .review-name {
          font-weight: 700;
          font-size: 13.5px;
          color: var(--text-heading);
        }

        .practitioner-profile-page .review-date {
          font-size: 11.5px;
          color: var(--text-muted);
        }

        .practitioner-profile-page .review-stars {
          color: var(--accent-gold);
          font-size: 13px;
          margin-bottom: 8px;
        }

        .practitioner-profile-page .review-card p {
          font-size: 13px;
          color: var(--text-body);
          margin: 0;
          line-height: 1.6;
        }

        @media (max-width: 700px) {
          .practitioner-profile-page .review-list {
            grid-template-columns: 1fr;
          }
        }

        .practitioner-profile-page .similar-scroll {
          display: flex;
          gap: 16px;
          overflow-x: auto;
          padding-bottom: 12px;
          margin: 0 -24px;
          padding-left: 24px;
          padding-right: 24px;
          scrollbar-width: none;
        }

        @media (max-width: 640px) {
          .practitioner-profile-page .similar-scroll {
            margin: 0 -18px;
            padding-left: 18px;
            padding-right: 18px;
          }
        }

        .practitioner-profile-page .similar-scroll::-webkit-scrollbar {
          display: none;
        }

        .practitioner-profile-page .sim-card {
          flex: 0 0 220px;
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 20px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.25s ease;
          box-shadow: var(--card-shadow);
          text-decoration: none !important;
        }

        .practitioner-profile-page .sim-card:hover {
          transform: translateY(-3px);
          box-shadow: var(--card-shadow-hover);
          border-color: var(--accent-emerald);
        }

        .practitioner-profile-page .sim-avatar {
          height: 136px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Fraunces', serif;
          font-size: 32px;
          color: #ffffff;
          font-weight: 700;
          overflow: hidden;
          background: var(--brand-primary);
        }

        .practitioner-profile-page .sim-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
        }

        .practitioner-profile-page .sim-body {
          padding: 14px 16px;
        }

        .practitioner-profile-page .sim-body h4 {
          font-size: 14px;
          font-weight: 700;
          margin-bottom: 3px;
          color: var(--text-heading);
        }

        .practitioner-profile-page .sim-body .r {
          font-size: 11.5px;
          color: var(--text-muted);
          font-weight: 500;
        }
      `}</style>

      <div className="practitioner-profile-page">
        <nav className="subnav">
          <div className="wrap">
            <a href="#about" className={activeTab === 'about' ? 'active' : ''} onClick={(e) => { e.preventDefault(); scrollToSection('about'); }}>About</a>
            <a href="#qualifications" className={activeTab === 'qualifications' ? 'active' : ''} onClick={(e) => { e.preventDefault(); scrollToSection('qualifications'); }}>Qualifications</a>
            <a href="#pricing" className={activeTab === 'pricing' ? 'active' : ''} onClick={(e) => { e.preventDefault(); scrollToSection('pricing'); }}>Classes &amp; Pricing</a>
            <a href="#availability" className={activeTab === 'availability' ? 'active' : ''} onClick={(e) => { e.preventDefault(); scrollToSection('availability'); }}>Availability</a>
            <a href="#reviews" className={activeTab === 'reviews' ? 'active' : ''} onClick={(e) => { e.preventDefault(); scrollToSection('reviews'); }}>Reviews</a>
          </div>
        </nav>

        {/* HERO */}
        <section className="profile-hero">
          <div className="wrap hero-layout">
            <div>
              <div className="id-block">
                <div className="id-avatar">
                  {instructor.profileIcon ? (
                    <img src={instructor.profileIcon} alt="Profile" />
                  ) : (
                    `${displayName.split(' ')[0]?.charAt(0) || 'A'}${displayName.split(' ').slice(-1)[0]?.charAt(0) || 'R'}`
                  )}
                </div>
                <div>
                  <div className="id-name-row">
                    <h1>{displayName}</h1>
                    <span className="verified">✓ Verified</span>
                  </div>
                  <div className="id-role">{instructor.role || 'Certified Yoga Instructor · Reiki Practitioner'}</div>
                  <div className="id-facts">
                    <span className="id-rating">★ {instructor.rating || 4.9} ({instructor.reviews || 212} reviews)</span>
                    <span>📍 {instructor.location}</span>
                    <span>🕐 {instructor.experience} years experience</span>
                    <span>💬 {instructor.languages && instructor.languages.length > 0 ? instructor.languages.join(", ") : "English"}</span>
                  </div>
                </div>
              </div>

              <div id="about" className="profile-card-box">
                <span className="eyebrow">About</span>
                <p className="about-text">{instructor.bio}</p>
              </div>

              <div id="qualifications" className="profile-card-box">
                <span className="eyebrow">Qualifications</span>
                <h2 style={{ marginTop: '4px', marginBottom: '18px' }}>What makes {instructor.firstname} a fit</h2>
                <div className="qual-list">
                  <div className="qual-item">
                    <div className="ic">★</div>
                    <div className="txt">
                      <b>{instructor.rating || 4.9} rating</b>
                      <span>{instructor.reviews || 212} reviews from students</span>
                    </div>
                  </div>
                  <div className="qual-item">
                    <div className="ic">⌛</div>
                    <div className="txt">
                      <b>{instructor.experience} years</b>
                      <span>Hands-on teaching experience</span>
                    </div>
                  </div>
                  <div className="qual-item">
                    <div className="ic">🌐</div>
                    <div className="txt">
                      <b>{instructor.languages?.length || 1} languages</b>
                      <span>{instructor.languages?.join(', ') || 'English'}</span>
                    </div>
                  </div>
                  <div className="qual-item">
                    <div className="ic">✓</div>
                    <div className="txt">
                      <b>Verified profile</b>
                      <span>Public instructor profile and booking readiness</span>
                    </div>
                  </div>
                </div>
                {instructor.Skills && instructor.Skills.length > 0 && (
                  <div className="lang-chips">
                    {instructor.Skills.slice(0, 8).map((skill: string, index: number) => (
                      <span key={index} className="lang-chip">{skill}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <aside className="booking-card">
              {serviceTabs.length > 0 ? (
                <>
                  {serviceTabs.length > 1 ? (
                    <div className="service-toggle">
                      {serviceTabs.map((tab) => (
                        <button
                          key={tab.key}
                          type="button"
                          onClick={() => setActivePanel(tab.key)}
                          className={`opt ${activePanel === tab.key ? 'active' : ''}`}
                        >
                          {tab.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="service-toggle">
                      <div className="opt active">{serviceTabs[0].label}</div>
                    </div>
                  )}

                  <div className="session-list">
                    {activeSessions.slice(0, 4).map((session: any) => {
                      const sessionPrice =
                        session?.classPrice?.session ??
                        session?.classPrice?.monthly ??
                        session?.classPrice?.yearly ??
                        session?.price ??
                        0;
                      const priceLabel =
                        session?.classPrice?.session
                          ? `₹${session.classPrice.session}/session`
                          : session?.classPrice?.monthly
                            ? `₹${session.classPrice.monthly}/month`
                            : session?.classPrice?.yearly
                              ? `₹${session.classPrice.yearly}/year`
                              : `₹${sessionPrice}`;

                      return (
                        <div key={session._id || session.classTitle} className="session-item">
                          <div>
                            <div className="title">{session.classTitle || selectedSession?.classTitle || 'Session'}</div>
                            <div className="meta">
                              {session.classCategory || session.purchaseStatus || (activePanel === 'consultation' ? 'Consultation' : 'Class')}
                            </div>
                          </div>
                          <div className="price">{priceLabel}</div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedSession ? (
                    <>
                      <div className="next-slot">
                        <span className="dot-live"></span>
                        <span>Next open slot: {formatSessionTime(selectedSession.classStartDate || selectedSession.availabilityFrom)}</span>
                      </div>

                      <Link
                        to={`/booking/${instructor.slug || id}/step1?classId=${selectedSession._id}`}
                        className="btn btn-primary btn-block"
                        style={{ marginTop: '10px' }}
                      >
                        Book a {activePanel === 'consultation' ? 'consultation' : 'session'}
                      </Link>

                      <a
                        href={`mailto:${instructor.useremail || ''}`}
                        className="btn btn-ghost btn-block"
                        style={{ marginTop: '10px' }}
                      >
                        Message {instructor.firstname}
                      </a>
                    </>
                  ) : (
                    <p className="service-muted">
                      No live booking slots are available right now.
                    </p>
                  )}
                </>
              ) : (
                <div>
                  <p className="service-muted">
                    Booking details are not available yet for this profile.
                  </p>
                </div>
              )}
            </aside>
          </div>
        </section>

        {/* ALL SESSIONS */}
        <section id="pricing">
          <div className="wrap">
            <div className="profile-card-box">
              <span className="eyebrow">Available Sessions</span>
              <h2 style={{ marginTop: '4px', marginBottom: '22px' }}>Every format {instructor.firstname} offers</h2>
              {instructor.upcomingClasses && instructor.upcomingClasses.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' }}>
                  {instructor.upcomingClasses.map((cls: any, i: number) => (
                    <div key={i} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '20px', padding: '22px', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <h3 style={{ fontSize: '17px', margin: 0, color: 'var(--text-heading)' }}>{cls.classTitle || 'Session'}</h3>
                        {cls.classCategory && (
                          <span style={{ fontSize: '11px', background: '#FFFFFF', border: '1px solid var(--border)', padding: '3px 10px', borderRadius: '9999px', color: 'var(--accent-emerald)', fontWeight: 700 }}>
                            {cls.classCategory}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '13px', color: 'var(--text-body)', marginBottom: '18px', flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>
                        {cls.classDescription || 'Join this session for a guided and transformative experience.'}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '14px', borderTop: '1px solid var(--border)' }}>
                        <div>
                          {cls.classPrice?.session ? (
                            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 700, color: 'var(--text-heading)' }}>
                              ₹{cls.classPrice.session}<span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>/session</span>
                            </div>
                          ) : cls.classPrice?.monthly ? (
                            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 700, color: 'var(--text-heading)' }}>
                              ₹{cls.classPrice.monthly}<span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 500, color: 'var(--text-muted)' }}>/month</span>
                            </div>
                          ) : (
                            <div style={{ fontFamily: "'Fraunces', serif", fontSize: '20px', fontWeight: 700, color: 'var(--text-heading)' }}>
                              ₹{cls.price || 499}
                            </div>
                          )}
                        </div>
                        <Link
                          to={`/booking/${instructor.slug || id}/step1?classId=${cls._id}`}
                          className="btn btn-primary"
                          style={{ padding: '8px 18px', fontSize: '12.5px' }}
                        >
                          Book Now
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', fontSize: '13.5px', margin: 0 }}>No sessions currently available for booking.</p>
              )}
            </div>
          </div>
        </section>

        {/* AVAILABILITY */}
        <section id="availability">
          <div className="wrap">
            <div className="profile-card-box">
              <span className="eyebrow">Availability</span>
              <h2 style={{ marginTop: '4px', marginBottom: '22px' }}>When you can book {instructor.firstname}</h2>
              <div className="qual-list">
                <div className="qual-item">
                  <div className="ic">☀</div>
                  <div className="txt">
                    <b>Morning / evening slots</b>
                    <span>Most sessions are scheduled around flexible hours.</span>
                  </div>
                </div>
                <div className="qual-item">
                  <div className="ic">📍</div>
                  <div className="txt">
                    <b>Online & in-person</b>
                    <span>Choose the format that works best for you.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* REVIEWS */}
        <section id="reviews">
          <div className="wrap">
            <div className="profile-card-box">
              <span className="eyebrow">Reviews</span>
              <h2 style={{ marginTop: '4px', marginBottom: '22px' }}>What students say</h2>
              <div className="review-summary">
                <div className="rs-score">4.9</div>
                <div className="rs-bars">
                  <div className="rs-bar-row"><span>5★</span><div className="rs-bar"><div className="rs-bar-fill" style={{ width: '84%' }}></div></div><span>178</span></div>
                  <div className="rs-bar-row"><span>4★</span><div className="rs-bar"><div className="rs-bar-fill" style={{ width: '12%' }}></div></div><span>26</span></div>
                  <div className="rs-bar-row"><span>3★</span><div className="rs-bar"><div className="rs-bar-fill" style={{ width: '3%' }}></div></div><span>6</span></div>
                  <div className="rs-bar-row"><span>2★</span><div className="rs-bar"><div className="rs-bar-fill" style={{ width: '1%' }}></div></div><span>2</span></div>
                </div>
              </div>
              <div className="review-list">
                <div className="review-card">
                  <div className="review-top">
                    <span className="review-name">Kavya Iyer</span>
                    <span className="review-date">2 weeks ago</span>
                  </div>
                  <div className="review-stars">★★★★★</div>
                  <p>Best beginner-friendly yoga classes I've tried. {instructor.firstname} explains alignment clearly without making you feel behind.</p>
                </div>
                <div className="review-card">
                  <div className="review-top">
                    <span className="review-name">Rohit Verma</span>
                    <span className="review-date">1 month ago</span>
                  </div>
                  <div className="review-stars">★★★★★</div>
                  <p>Booked the prenatal batch — felt genuinely safe and well-guided through every trimester.</p>
                </div>
                <div className="review-card">
                  <div className="review-top">
                    <span className="review-name">Sana R.</span>
                    <span className="review-date">1 month ago</span>
                  </div>
                  <div className="review-stars">★★★★☆</div>
                  <p>Great instructor, only wish there were more evening slots for the monthly plan.</p>
                </div>
                <div className="review-card">
                  <div className="review-top">
                    <span className="review-name">Arjun K.</span>
                    <span className="review-date">2 months ago</span>
                  </div>
                  <div className="review-stars">★★★★★</div>
                  <p>The Reiki add-on session after my yoga class was unexpectedly the highlight of my week.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SIMILAR INSTRUCTORS */}
        <section style={{ paddingBottom: '60px' }}>
          <div className="wrap">
            <div className="profile-card-box">
              <span className="eyebrow">You might also like</span>
              <h2 style={{ marginTop: '4px', marginBottom: '20px' }}>Similar {activeCategory} Instructors</h2>
              <div className="similar-scroll">
                {similarInstructors.length > 0 ? (
                  similarInstructors.map((s: any) => (
                    <Link
                      to={s.slug ? `/public/instructor-profile/${s.slug}` : '#'}
                      key={s.name}
                      className="sim-card block"
                      state={{
                        sourcePillar: pillarMeta,
                        sourceSpeciality: specialityLabel,
                        sourceName: s.name,
                      }}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      <div className="sim-avatar" style={{ background: s.c }}>
                        {s.profileIcon ? <img src={s.profileIcon} alt={s.name} className="w-full h-full object-cover" /> : s.i}
                      </div>
                      <div className="sim-body">
                        <h4>{s.name}</h4>
                        <div className="r">{s.r}</div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <p style={{ color: 'var(--text-muted)', fontSize: '13.5px', fontStyle: 'italic', margin: 0 }}>No similar instructors found.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
